import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShieldAlert, ArrowRight } from "lucide-react";
import { registerRiskGate, setRiskConsent } from "@/lib/riskGate";
import { CornerFrame } from "./CornerFrame";

const POINTS = [
  "TradrQuant is not a financial advisor — content is educational only.",
  "Trading involves substantial risk. You can lose some or all of your capital.",
  "Alerts & pattern detections are not signals to buy or sell. Do your own research.",
  "Past & sample performance is not indicative of future results.",
];

export const RiskGate = () => {
  const [url, setUrl] = useState(null);
  const [go, setGo] = useState(null);

  useEffect(() => {
    registerRiskGate((targetUrl, openFn) => {
      setUrl(targetUrl);
      setGo(() => openFn);
    });
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setUrl(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const accept = () => {
    setRiskConsent();
    if (go && url) go(url);
    setUrl(null);
  };

  return (
    <AnimatePresence>
      {url && (
        <motion.div
          className="fixed inset-0 z-[9997] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => setUrl(null)} data-testid="risk-gate-modal"
        >
          <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" />
          <motion.div
            className="relative w-full max-w-md border border-white/[0.10] bg-[#0d0d0d] overflow-hidden"
            initial={{ y: 24, scale: 0.97 }} animate={{ y: 0, scale: 1 }} exit={{ y: 24, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 300, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
          >
            <CornerFrame />
            <div className="flex items-center gap-2 border-b border-white/10 bg-[#111111] px-4 py-3">
              <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
              <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
              <span className="h-2.5 w-2.5 rounded-full bg-[#BACD1C]/60" />
              <span className="ml-3 font-term text-xs text-white/45 tracking-[0.15em] lowercase">risk_ack.sh</span>
              <button onClick={() => setUrl(null)} data-testid="risk-gate-close" className="ml-auto text-white/40 hover:text-[#BACD1C] transition-colors"><X size={16} /></button>
            </div>

            <div className="p-7">
              <div className="flex items-center gap-2 font-term text-[11px] uppercase tracking-[0.2em] text-[#BACD1C]/70">
                <ShieldAlert size={14} /> // before you continue
              </div>
              <h3 className="mt-3 font-display font-bold uppercase tracking-tight text-2xl leading-tight">
                Understand the risk
              </h3>
              <ul className="mt-5 space-y-2.5">
                {POINTS.map((p) => (
                  <li key={p} className="flex gap-2.5 font-term text-[13px] text-white/60 leading-snug">
                    <span className="text-[#BACD1C] mt-0.5 shrink-0">▸</span> {p}
                  </li>
                ))}
              </ul>

              <button
                onClick={accept}
                data-testid="risk-gate-accept"
                className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#BACD1C] text-black font-term text-sm uppercase tracking-[0.12em] px-6 py-4 hover:bg-white active:scale-[0.98] transition-[background-color,transform] duration-300"
              >
                I understand the risks — continue <ArrowRight size={16} />
              </button>
              <p className="mt-4 font-term text-[11px] text-white/30 leading-relaxed">
                By continuing you accept our{" "}
                <Link to="/risk" className="text-[#BACD1C]/70 hover:text-[#BACD1C] underline underline-offset-2">Risk Notice &amp; Terms</Link>.
                This acknowledgement is stored on your device.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
